import { Injectable } from '@angular/core';
import {
    CanActivateChild,
    Router,
    ActivatedRouteSnapshot,
    RouterStateSnapshot,
} from '@angular/router';

import { BuildVersionService } from '@intranet/core/services/build-version.service';

/**
 * Picks up a newly deployed build on the user's next page change.
 *
 * A tab left open keeps running the bundle it loaded until it's refreshed.
 * Once BuildVersionService has seen a newer build on the server, this guard
 * swaps the next in-app navigation for a full page load of the same URL, so
 * the user lands on the page they asked for — just on the new version.
 *
 * Runs as a guard (rather than on a router event) so the in-app navigation
 * can be cancelled cleanly: the old bundle never starts building the next
 * page. Guards only re-run when the route path/params change, so
 * query-param-only updates (filters writing to the URL) never trigger it.
 */
@Injectable({ providedIn: 'root' })
export class NewVersionReloadGuard implements CanActivateChild {
    constructor(
        private buildVersion: BuildVersionService,
        private router: Router,
    ) {}

    canActivateChild(
        _childRoute: ActivatedRouteSnapshot,
        state: RouterStateSnapshot
    ): boolean {
        const nav = this.router.getCurrentNavigation();
        // Back/forward: the browser has already moved to this history entry,
        // so replace it rather than pushing a duplicate. Same for navigations
        // that asked to replace the current entry.
        const replace = nav?.trigger === 'popstate' || !!nav?.extras?.replaceUrl;
        const reloading = this.buildVersion.reloadForNavigation(state.url, replace);
        return !reloading;
    }
}
